import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode
} from 'react';

import { PostJSON, GetJSON } from '../utils/request';

// User type based on the backend UserRecord (without password)
export interface User {
  id: number;
  email: string;
  isAdmin: boolean;
  firstName?: string;
  lastName?: string;
  accountId: number;
  createdAt: Date;
  updatedAt: Date;
}

// Account terms type
export interface AccountTerms {
  aide?: string;
}

// Account configs type
export interface AccountConfigs {
  checkinUpdateEmail?: string;
  checkinWeeklyDigestEmail?: string;
  checkinWeeklyDigestDayOfWeek?: number; // 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
  clientDigestCopyEmail?: string;
  terms?: AccountTerms;
  preferredAreaCodes?: string[];
}

// Account type based on the backend AccountRecord
export interface Account {
  id: number;
  name: string;
  configs: AccountConfigs | null;
  createdAt: Date;
  updatedAt: Date;
}

// Auth context state
export interface AuthState {
  user: User | null;
  account: Account | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Auth context actions
export interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, accountName: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

// Combined auth context type
export interface AuthContextType extends AuthState, AuthActions {}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated
  const isAuthenticated = user !== null;

  // Clear error function
  const clearError = useCallback(() => setError(null), []);

  // Refresh user data from server
  const refreshUser = async (isInitialLoad: boolean = false) => {
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      
      const response = await GetJSON<{ user: User | null; account: Account | null }>('/api/account/me');
      setUser(response.user);
      setAccount(response.account);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      setUser(null);
      setAccount(null);
      setError(err instanceof Error ? err.message : 'Failed to refresh user');
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (email: string, password: string) => {
    if (isFetching) return;

    try {
      setIsFetching(true);
      
      const response = await PostJSON<{ success: boolean; user: User }>('/api/account/auth', {
        email,
        password,
      });
      
      if (response.success) {
        setError(null);
        setUser(response.user);
        // Refresh user data to get account information
        await refreshUser();
      } else {
        throw new Error('Login failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
    } finally {
      setIsFetching(false);
    }
  };

  // Register function
  const register = async (
    email: string, 
    password: string, 
    accountName: string, 
    firstName?: string, 
    lastName?: string
  ) => {
    if (isFetching) return;
    
    try {
      setIsFetching(true);

      const response = await PostJSON<{ success: boolean; user: User }>('/api/account/register', {
        email,
        password,
        accountName,
        firstName,
        lastName,
      });
      
      if (response.success) {
        setError(null);
        setUser(response.user);
        // Refresh user data to get account information
        await refreshUser();
      } else {
        throw new Error('Registration failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
    } finally {
      setIsFetching(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError(null);
      
      await PostJSON<{ success: boolean }>('/api/account/logout');
      setUser(null);
      setAccount(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Even if logout fails on server, clear local state
      setUser(null);
      setAccount(null);
      setError(err instanceof Error ? err.message : 'Logout failed');
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    void refreshUser();
  }, []);

  // Context value
  const contextValue: AuthContextType = {
    // State
    user,
    account,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    login,
    register,
    logout,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

// Export the context for advanced usage
export { AuthContext };
